// Typed wrappers for the wave-5 endpoints (decision D23): approvals and council reviews, production accounting,
// planning runs, parallelism, playbooks, support contacts and channels, and the MFA verification of a pending
// session. Paths and bodies follow docs/delivery/wave5-plan.md §3; the API enforces every permission.
import { api } from "./client.ts";
import type {
  Approval,
  ApprovalMetrics,
  ApprovalState,
  CouncilReview,
  DecideApprovalResponse,
  LeaderboardRow,
  LinkCode,
  NotificationChannel,
  NotificationChannelInput,
  Parallelism,
  PlanningRun,
  Playbook,
  ProjectProduction,
  ServiceGrant,
  SupportChannel,
  TaskProduction,
  UserContact,
  UserContactInput,
  UserNotification,
} from "./types-wave5.ts";

const enc = encodeURIComponent;
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

// ---------- Approvals and council ----------

export const listProjectApprovals = (projectId: string, state: ApprovalState | undefined, signal?: AbortSignal) =>
  api<{ items: Approval[] }>(`/projects/${enc(projectId)}/approvals`, { query: { state }, ...withSignal(signal) });
/** The approvals of every project the person may see (pending first), optionally one state. */
export const listApprovals = (state: ApprovalState | undefined, signal?: AbortSignal) => api<{ items: Approval[] }>("/approvals", { query: { state }, ...withSignal(signal) });
export const decideApproval = (id: string, body: { option: string; note?: string }) => api<DecideApprovalResponse>(`/approvals/${enc(id)}/decide`, { method: "POST", body });
// D41-C4: delegated grants. Listing needs an organization maintainer; revoking needs an organization administrator.
export const listServiceGrants = (q: { projectId?: string; live?: boolean } = {}, signal?: AbortSignal) =>
  api<{ items: ServiceGrant[] }>("/service-grants", { query: { project_id: q.projectId, live: q.live ? true : undefined }, ...withSignal(signal) });
export const revokeServiceGrant = (id: string, body: { reason: string }) => api<ServiceGrant>(`/service-grants/${enc(id)}/revoke`, { method: "POST", body });
export const getProjectApprovalMetrics = (projectId: string, signal?: AbortSignal) => api<ApprovalMetrics>(`/projects/${enc(projectId)}/approvals/metrics`, withSignal(signal));
export const listTaskCouncilReviews = (taskId: string, signal?: AbortSignal) => api<{ items: CouncilReview[] }>(`/tasks/${enc(taskId)}/council-reviews`, withSignal(signal));

// ---------- Production accounting ----------

export const getTaskProduction = (taskId: string, signal?: AbortSignal) => api<TaskProduction>(`/tasks/${enc(taskId)}/production`, withSignal(signal));
export const getProjectProduction = (projectId: string, signal?: AbortSignal) => api<ProjectProduction>(`/projects/${enc(projectId)}/production`, withSignal(signal));
export const getLeaderboard = (days: number, signal?: AbortSignal) => api<{ since: string; items: LeaderboardRow[] }>("/agents/leaderboard", { query: { days }, ...withSignal(signal) });

// ---------- Planning agents ----------

/** D36.4: `attachment_ids` = composer attachments sent with the brief to the planner (the caller's own, Home or this project). */
export const startPlanningRun = (projectId: string, body: { prd_version_id?: string; attachment_ids?: string[] } = {}) =>
  api<PlanningRun>(`/projects/${enc(projectId)}/planning-runs`, { method: "POST", body });
export const listPlanningRuns = (projectId: string, signal?: AbortSignal) => api<{ items: PlanningRun[] }>(`/projects/${enc(projectId)}/planning-runs`, withSignal(signal));
export const getParallelism = (projectId: string, signal?: AbortSignal) => api<Parallelism>(`/projects/${enc(projectId)}/parallelism`, withSignal(signal));
export const listPlaybooks = (signal?: AbortSignal) => api<{ items: Playbook[] }>("/playbooks", withSignal(signal));

// ---------- Support channels ----------

export const getMyContact = (signal?: AbortSignal) => api<UserContact>("/me/contact", withSignal(signal));
export const updateMyContact = (body: UserContactInput) => api<UserContact>("/me/contact", { method: "PUT", body });
export const createLinkCode = (channel: Exclude<SupportChannel, "email">) => api<LinkCode>("/me/contact/link-code", { method: "POST", body: { channel } });
export const listMyNotifications = (signal?: AbortSignal) => api<{ items: UserNotification[] }>("/me/notifications", withSignal(signal));
export const listNotificationChannels = (signal?: AbortSignal) => api<{ items: NotificationChannel[] }>("/notification-channels", withSignal(signal));
export const updateNotificationChannel = (channel: SupportChannel, body: NotificationChannelInput) =>
  api<NotificationChannel>(`/notification-channels/${enc(channel)}`, { method: "PUT", body });

// ---------- Auth ----------

/** A signed-in session whose MFA is still pending (e.g. opened by a magic link) confirms the TOTP code. */
export const verifyMfa = (code: string) => api<{ verified: true; expires_at: string }>("/auth/mfa/verify", { method: "POST", body: { code } });
